/**
 * Route Details Modal component for ServiceabilityInfoStep
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Select } from 'antd';
import { Box } from 'atoms';
import FormField from './FormField';
import {
	StyledModal,
	StyledInput,
	StyledSelect,
	RemoveButton,
	SaveButton,
} from './ServiceabilityInfoStep.styled';
import { MOVEMENT_TYPE_OPTIONS } from './ServiceabilityInfoStep.constants';

const { Option } = Select;

function RouteDetailsModal({
	visible,
	selectedRoute,
	editingRoute,
	setEditingRoute,
	disabled,
	addingRoute,
	onClose,
	onSave,
	onRemove,
	sourceCityOptions = [],
	destinationCityOptions = [],
	loadingCities = false,
}) {
	/**
	 * Handle typeOfMovement changes for intracity logic in modal
	 * - When type is "Intracity" and source city is selected, auto-fill destination city
	 * - When type changes from "Intracity" to "Intercity", clear destination city
	 */
	useEffect(() => {
		if (!editingRoute) {
			return;
		}

		const { typeOfMovement, sourceCity, destinationCity } = editingRoute;

		if (typeOfMovement === 'Intracity' && sourceCity && destinationCity !== sourceCity) {
			// If source city is selected, auto-fill destination city
			setEditingRoute({ ...editingRoute, destinationCity: sourceCity });
		} else if (typeOfMovement === 'Intercity' && destinationCity === sourceCity) {
			// If switching from Intracity to Intercity, clear destination city
			// Only clear if destination was previously set to source (intracity scenario)
			setEditingRoute({ ...editingRoute, destinationCity: '' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editingRoute?.typeOfMovement, editingRoute?.sourceCity]);

	if (!selectedRoute || !editingRoute) {
		return null;
	}

	return (
		<StyledModal
			title="Serviceable Route Details"
			open={visible}
			onCancel={onClose}
			width={600}
			footer={[
				<RemoveButton
					key="remove"
					onClick={onRemove}
					disabled={addingRoute}
					loading={addingRoute}
				>
					Remove Route
				</RemoveButton>,
				<SaveButton
					key="save"
					onClick={onSave}
					disabled={addingRoute}
					loading={addingRoute}
				>
					Save Changes
				</SaveButton>,
			]}
		>
			<Box>
				<FormField label="Vendor Name">
					<StyledInput value={selectedRoute.vendorName || ''} disabled />
				</FormField>

				<FormField label="Type of Movement" required>
					<StyledSelect
						value={editingRoute.typeOfMovement}
						onChange={(value) =>
							setEditingRoute({ ...editingRoute, typeOfMovement: value })
						}
						disabled={disabled || addingRoute}
						placeholder="Select"
					>
						{MOVEMENT_TYPE_OPTIONS.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</StyledSelect>
				</FormField>

				<FormField label="Source City" required>
					<StyledSelect
						value={editingRoute.sourceCity}
						onChange={(value) => {
							const updatedRoute = { ...editingRoute, sourceCity: value };
							// Auto-fill destination city when type is Intracity
							if (editingRoute.typeOfMovement === 'Intracity' && value) {
								updatedRoute.destinationCity = value;
							}
							setEditingRoute(updatedRoute);
						}}
						disabled={disabled || addingRoute || loadingCities}
						placeholder="Select"
						showSearch
						loading={loadingCities}
						filterOption={(input, option) =>
							(option?.children || '').toLowerCase().includes(input.toLowerCase())
						}
						notFoundContent={loadingCities ? 'Loading cities...' : 'No cities found'}
					>
						{sourceCityOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</StyledSelect>
				</FormField>

				<FormField label="Destination City" required>
					<StyledSelect
						value={editingRoute.destinationCity}
						onChange={(value) =>
							setEditingRoute({ ...editingRoute, destinationCity: value })
						}
						disabled={
							disabled ||
							addingRoute ||
							loadingCities ||
							(editingRoute.typeOfMovement === 'Intracity' && editingRoute.sourceCity)
						}
						placeholder="Select"
						showSearch
						loading={loadingCities}
						filterOption={(input, option) =>
							(option?.children || '').toLowerCase().includes(input.toLowerCase())
						}
						notFoundContent={loadingCities ? 'Loading cities...' : 'No cities found'}
					>
						{destinationCityOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</StyledSelect>
				</FormField>
			</Box>
		</StyledModal>
	);
}

RouteDetailsModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	selectedRoute: PropTypes.object,
	editingRoute: PropTypes.object,
	setEditingRoute: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	addingRoute: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	sourceCityOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.string,
		})
	),
	destinationCityOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.string,
		})
	),
	loadingCities: PropTypes.bool,
};

export default RouteDetailsModal;
